import React, { useState } from 'react';
import { Link, NavLink, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useKeyPress } from 'ahooks';
import { Avatar, Dropdown, Modal, Space } from 'antd';
import {
  LoadingOutlined, FileTextOutlined, FolderOutlined, SettingOutlined, InteractionOutlined, ControlOutlined, LogoutOutlined, UserSwitchOutlined, ToolOutlined, CaretDownOutlined, ApartmentOutlined,
  QuestionCircleOutlined,
  BookOutlined,
  RocketOutlined,
  MailOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import theme from 'shared/constants/theme.json';
import { HIDE_USER_PROFILE, SUPER_USER, VIEW_DASHBOARDS, VIEW_SECTION_FILES, VIEW_SECTION_REPORTS } from 'shared/constants/permissions';
import useUser from 'hooks/useUser';
import useCustomer from 'hooks/useCustomer';
import useAuth from 'hooks/useAuth';
import CustomerSelect from 'components/CustomerSelect';
import ExtellioLogo from 'components/ExtellioLogo';
import globalMessages from 'translations/messages';
import messages from './messages';
import './navigation.less';

const isTyping = e => e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true';

const getInitials = email => email?.split('@')[0].split('.').map(s => s[0]).join('').toUpperCase();

function getUserMenu({ user, stopImpersonation, customer, setCustomerSelectOpen, canChangeCustomer, logout, isSuperUser, canAccessUserProfile }) {
  const items = [];

  const userMenu = {
    key: 'user',
    type: 'group',
    label: (
      <Space>
        User
        {user?.email}
      </Space>
    ),
    children: [],
  };

  if (canAccessUserProfile) {
    userMenu.children.push({
      label: (
        <Link to="/user">
          User settings
        </Link>
      ),
      key: 'userProfile',
      icon: <SettingOutlined />,
    });
  }

  if (user?.impersonator) {
    userMenu.children.push({
      label: <FormattedMessage {...messages.stopImpersonation} />,
      icon: <UserSwitchOutlined />,
      key: 'stopImpersonation',
      onClick: stopImpersonation,
    });
  }

  if (userMenu.children.length > 0) {
    items.push(userMenu);
    items.push({
      type: 'divider',
    });
  }

  const accountMenu = {
    key: 'account',
    type: 'group',
    label: (
      <Space wrap>
        Account
        {customer?.name}
      </Space>
    ),
    children: [],
  };

  if (customer) {
    accountMenu.children.push({
      label: (
        <Link to="/account">
          <FormattedMessage {...globalMessages.customerSettings} />
        </Link>
      ),
      key: 'customerSettings',
      icon: <SettingOutlined />,
    });
  }

  if (canChangeCustomer) {
    accountMenu.children.push({
      label: <FormattedMessage {...globalMessages.changeCustomer} />,
      key: 'changeCustomer',
      onClick: () => setCustomerSelectOpen(true),
      icon: <InteractionOutlined />,
    });
  }

  items.push(accountMenu);
  items.push({
    type: 'divider',
  });

  if (isSuperUser) {
    items.push({
      label: (
        <Link to="/administration">
          <FormattedMessage {...globalMessages.administration} />
        </Link>
      ),
      key: 'administration',
      icon: <ControlOutlined />,
    });
  }

  items.push({
    label: <FormattedMessage {...globalMessages.logout} />,
    key: 'logout',
    onClick: () => logout(),
    icon: <LogoutOutlined />,
  });

  return items;
}

const supportMenuItems = [{
  key: 'knowledge',
  label: <a href="https://academy.extellio.com/knowledge-base" target="_blank">Knowledge base</a>,
  icon: <BookOutlined />,
}, {
  key: 'digitalonboarding',
  label: <a href="https://academy.extellio.com/digital-onboarding" target="_blank">Digital onboarding</a>,
  icon: <RocketOutlined />,
}, {
  key: 'contact',
  label: <a href="https://academy.extellio.com/contact-support" target="_blank">Contact us</a>,
  icon: <MailOutlined />,
}];

const mainMenuItems = [
  {
    label: 'Dashboards',
    url: '/dashboards',
    icon: <DashboardOutlined />,
    permission: VIEW_DASHBOARDS,
  },
  {
    label: 'Data sources',
    url: '/data-sources',
    icon: <ApartmentOutlined />,
  },
  {
    label: 'Advanced tools',
    url: '/tools',
    icon: <ToolOutlined />,
  },
  {
    label: <FormattedMessage {...globalMessages.reports} />,
    url: '/reports',
    icon: <FileTextOutlined />,
    permission: VIEW_SECTION_REPORTS,
  },
  {
    label: <FormattedMessage {...globalMessages.files} />,
    url: '/files',
    icon: <FolderOutlined />,
    permission: VIEW_SECTION_FILES,
  },
];

const keyMapping = {
  d: {
    path: '/dashboards',
    permission: VIEW_DASHBOARDS,
  },
  s: {
    path: '/data-sources',
  },
  a: {
    path: '/administration',
    permission: SUPER_USER,
  },
  r: {
    path: '/reports',
    permission: VIEW_SECTION_REPORTS,
  },
  f: {
    path: '/files',
    permission: VIEW_SECTION_FILES,
  },
};

const MainNavigation = () => {
  const { user, isSuperUser, changeCustomer, hasPermission } = useUser();
  const { customer, loading, hasCustomerPermission } = useCustomer({ extraFields: 'dataSources { id title type }' });
  const history = useHistory();
  const [customerSelectOpen, setCustomerSelectOpen] = useState(false);
  const { logout, stopImpersonation } = useAuth();

  const canChangeCustomer = isSuperUser || user?.customerAccess?.length > 1;
  // const theme = user?.globalSettings?.theme || 'default';

  // useEffect(() => {
  //   if (canChangeTheme) {
  //     if (theme === 'extellio') {
  //       document.getElementsByTagName('html')[0].classList.add('extellio');
  //     } else {
  //       document.getElementsByTagName('html')[0].classList.remove('extellio');
  //     }
  //   }
  // }, [theme]);

  useKeyPress(['ctrl.space'], () => {
    if (canChangeCustomer) {
      setCustomerSelectOpen(true);
    }
  });

  useKeyPress(Object.keys(keyMapping), (e) => {
    if (!isTyping(e)) {
      const { path, permission } = keyMapping[e.key];
      if (!permission || hasPermission(permission)) {
        history.push(path);
      }
    }
  });

  const onCustomerChange = async (id) => {
    await changeCustomer(id);
    setCustomerSelectOpen(false);
    history.push('/data-sources');
  };

  // const onThemeChange = async () => {
  //   await updateUserSetting({ setting: 'theme', value: theme === 'default' ? 'extellio' : 'default' });
  // };

  const userMenuItems = getUserMenu({
    user,
    stopImpersonation,
    customer,
    setCustomerSelectOpen,
    canChangeCustomer,
    logout,
    isSuperUser,
    canAccessUserProfile: isSuperUser || !hasPermission(HIDE_USER_PROFILE),
  });

  return (
    <div className="nav">
      <div className="nav__left">
        <NavLink exact to="/" className="nav__logo">
          <ExtellioLogo />
        </NavLink>

        {mainMenuItems.filter(item => !item.permission || hasPermission(item.permission) || hasCustomerPermission(item.permission)).map(item => (
          <NavLink
            key={item.url}
            to={item.url}
            exact={item.exact}
            className="nav__item"
          >
            <Space>
              {item.icon}
              {item.label}
            </Space>
          </NavLink>
        ))}
      </div>
      <div className="nav__right">
        <Dropdown menu={{ items: supportMenuItems }}>
          <a className="nav__item">
            <QuestionCircleOutlined />
          </a>
        </Dropdown>

        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" overlayStyle={{ maxWidth: 400 }}>
          <a className="nav__user nav__item">
            <Space>
              {user && <Avatar className="nav__user-avatar" size={24} gap={4} style={{ backgroundColor: user?.impersonator ? theme['warning-color'] : 'white' }}>{getInitials(user?.email)}</Avatar>}
              <div className="nav__user-name">
                {customer?.name}
              </div>
              {loading ? <LoadingOutlined /> : <CaretDownOutlined />}
            </Space>
          </a>
        </Dropdown>
        <Modal
          title={<FormattedMessage {...globalMessages.changeCustomer} />}
          open={customerSelectOpen}
          onCancel={() => setCustomerSelectOpen(false)}
          footer={null}
          destroyOnClose
        >
          {customerSelectOpen && (
            <CustomerSelect
              value={customer?.id}
              onChange={onCustomerChange}
              allowClear
              autoFocus
              popupMatchSelectWidth
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default MainNavigation;
